import {Button, IconButton, MobileMenu, useTranslationContext} from 'platform/components';
import {Heading} from 'platform/foundation-tw';

import {useState} from 'react';
import {createPortal} from 'react-dom';

import {useOnMount} from 'shared/web';

import type {EagColumnGroup, EagColumnOrGroup} from '../../../../types/EagColumnGroup';
import type {FilterState} from '../../../../types/FilterState';
import {isEagColumnGroup} from '../../../../utils/isEagColumnGroup';
import {FilterContentArea} from '../FilterContentArea/FilterContentArea';

type MobileFilterPanelProps = {
  columnConfig: EagColumnOrGroup[];
  filterState: FilterState;
  onFilterChange: (key: string, value: unknown) => void;
  onResetFilters: () => void;
  isOpen: boolean;
  onClose: () => void;
};

type CategoryOption = {
  value: string;
  label: string;
};

// APP_HEADER_HEIGHT = 12 theme units = 48px = top-12 in Tailwind.
// Matches the DMS Layout grid-template-rows and APP_HEADER_HEIGHT in activity-panel.
const DRAWER_CLASS =
  'fixed top-12 inset-x-0 bottom-0 bg-white flex flex-col overflow-hidden z-(--z-header-popper)';

const buildCategoryOptions = (columnConfig: EagColumnOrGroup[]): CategoryOption[] => {
  const seen = new Set<string>();
  const options: CategoryOption[] = [];
  let hasStandalone = false;

  for (const item of columnConfig) {
    if (isEagColumnGroup(item)) {
      const group = item as EagColumnGroup;
      if (!seen.has(group.groupId)) {
        seen.add(group.groupId);
        options.push({value: group.groupId, label: group.headerName});
      }
    } else {
      hasStandalone = true;
    }
  }

  if (hasStandalone) {
    options.push({value: '__standalone__', label: 'Other'});
  }

  return options;
};

/**
 * Mobile filter drawer — renders as a portal over the entire app viewport.
 *
 * Portal target: `document.body`
 * Position: `fixed top-12 (48px = app header height) inset-x-0 bottom-0`
 *
 * Layout:
 * ```
 * ┌─────────────────────────────────┐  ← top: 48px (app header bottom)
 * │ Filters (Heading size=4)      ✕ │  ← PINNED header (border-b)
 * ├─────────────────────────────────┤
 * │ [MobileMenu: category ▼]        │  ← SCROLLABLE body (not pinned)
 * │  filter widgets grid...         │
 * ├─────────────────────────────────┤
 * │ [Reset filters]                 │  ← PINNED footer (border-t separator)
 * └─────────────────────────────────┘  ← bottom: 0
 * ```
 */
export function MobileFilterPanel(props: MobileFilterPanelProps) {
  const t = useTranslationContext();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  const categoryOptions = buildCategoryOptions(props.columnConfig);

  useOnMount(() => {
    if (categoryOptions.length > 0) {
      setSelectedCategoryId(categoryOptions[0].value);
    }
  });

  if (!props.isOpen) {
    return null;
  }

  return createPortal(
    <div className={DRAWER_CLASS}>
      {/* Pinned header — same Heading size as platform Card title */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
        <Heading size={4}>{t('page.datagrid.labels.filters')}</Heading>
        <IconButton size="small" icon="navigation/close" onClick={props.onClose} />
      </div>

      {/* Scrollable body: MobileMenu (category picker) + filter content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-3 border-b border-gray-100">
          <MobileMenu
            options={categoryOptions}
            value={selectedCategoryId}
            placeholder="Select category"
            onClick={setSelectedCategoryId}
          />
        </div>
        <FilterContentArea
          columnConfig={props.columnConfig}
          selectedCategoryId={selectedCategoryId}
          filterState={props.filterState}
          onFilterChange={props.onFilterChange}
          searchTerm=""
          noScroll
        />
      </div>

      {/* Pinned footer — Reset button with visible border separator */}
      <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200">
        <Button
          variant="secondary"
          isFullWidth
          onClick={props.onResetFilters}
          title="Reset filters"
        />
      </div>
    </div>,
    document.body
  );
}
